import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FiActivity, FiUser, FiGitBranch, FiPackage, FiBarChart2 } from 'react-icons/fi';
import { useAnalytics } from '../../hooks/useAnalytics';

interface NavCard {
  key: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  stat: string | number;
  statLabel: string;
  gradient: string;
  glowColor: string;
}

const DashboardNav: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const persona = useAnalytics({ entityType: 'persona' });
  const workflow = useAnalytics({ entityType: 'workflow' });
  const artifactory = useAnalytics({ entityType: 'artifactory' });

  const cards: NavCard[] = [
    {
      key: 'overview',
      title: 'The Pulse',
      icon: FiActivity,
      path: '/dashboards',
      stat: '\u2014',
      statLabel: 'heartbeat',
      gradient: 'from-indigo-600 to-purple-600',
      glowColor: 'rgba(139,92,246,0.4)',
    },
    {
      key: 'personas',
      title: 'Forge Monitor',
      icon: FiUser,
      path: '/dashboards/personas',
      stat: persona.summary?.total ?? '...',
      statLabel: `${persona.summary?.active_30d ?? 0} forging`,
      gradient: 'from-purple-600 to-purple-500',
      glowColor: 'rgba(139,92,246,0.4)',
    },
    {
      key: 'workflows',
      title: 'Phase Watch',
      icon: FiGitBranch,
      path: '/dashboards/workflows',
      stat: workflow.summary?.total ?? '...',
      statLabel: `${workflow.summary?.total_executions ?? 0} pipeline runs`,
      gradient: 'from-blue-600 to-blue-500',
      glowColor: 'rgba(37,99,235,0.4)',
    },
    {
      key: 'artifactory',
      title: 'Blueprint Vault',
      icon: FiPackage,
      path: '/dashboards/artifactory',
      stat: artifactory.summary?.total ?? '...',
      statLabel: 'blueprints',
      gradient: 'from-cyan-600 to-cyan-500',
      glowColor: 'rgba(6,182,212,0.4)',
    },
    {
      key: 'user',
      title: 'My Dashboard',
      icon: FiBarChart2,
      path: '/dashboards/me',
      stat: '\u2014',
      statLabel: 'your profile',
      gradient: 'from-sky-600 to-sky-500',
      glowColor: 'rgba(14,165,233,0.4)',
    },
  ];

  // Determine active card from URL
  const getActiveKey = (): string => {
    const path = location.pathname;
    if (path.startsWith('/dashboards/personas')) return 'personas';
    if (path.startsWith('/dashboards/workflows')) return 'workflows';
    if (path.startsWith('/dashboards/artifactory')) return 'artifactory';
    if (path.startsWith('/dashboards/user') || path === '/dashboards/me') return 'user';
    return 'overview';
  };

  const activeKey = getActiveKey();

  return (
    <div className="grid grid-cols-5 gap-3 mb-6">
      {cards.map(card => {
        const isActive = card.key === activeKey;
        return (
          <button
            key={card.key}
            onClick={() => navigate(card.path)}
            className={`relative px-4 py-3 rounded-xl text-white text-left transition-all duration-300 overflow-hidden group ${
              isActive
                ? `bg-gradient-to-br ${card.gradient} scale-[1.02] ring-2 ring-white/20`
                : darkMode
                  ? 'bg-gray-800/60 hover:bg-gray-800 text-gray-300 hover:ring-1 hover:ring-white/10'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            } ${!isActive ? 'hover:scale-[1.01] hover:translate-y-[-1px]' : ''}`}
            style={isActive ? {
              boxShadow: `0 4px 20px ${card.glowColor}, 0 0 60px ${card.glowColor.replace('0.4', '0.1')}`,
            } : undefined}
          >
            {/* Hatching on active */}
            {isActive && (
              <div className="absolute inset-0 opacity-[0.04]" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.5) 10px, rgba(255,255,255,0.5) 20px)',
              }} />
            )}
            <div className="relative z-10 flex items-center gap-3">
              <div className={`p-1.5 rounded-lg ${isActive ? 'bg-white/15 shadow-inner' : darkMode ? 'bg-gray-700/50' : 'bg-gray-300/50'}`}>
                <card.icon className={`w-4 h-4 ${isActive ? '' : darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-bold truncate ${isActive ? 'drop-shadow-sm' : ''}`}>{card.title}</p>
                <div className="flex items-baseline gap-1.5">
                  {card.key !== 'overview' && (
                    <span className={`text-lg font-bold leading-none ${isActive ? '' : darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {card.stat}
                    </span>
                  )}
                  <span className={`text-[10px] ${isActive ? 'opacity-70' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {card.statLabel}
                  </span>
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default DashboardNav;
